import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, EmptyState, StatusBadge } from "@/components/Ui";
import { fmtDate } from "@/lib/time";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";
import { Pause, Play, Loader2 } from "lucide-react";

export default function AdminSubscriptions() {
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(null);

    const load = useCallback(() => {
        api.get("/subscriptions").then(r => setItems(r.data)).catch(e => toast.error(apiError(e)));
    }, []);

    useEffect(() => { load(); }, [load]);

    const suspend = async (s) => {
        if (!window.confirm(`Suspend "${s.tenant_name}" subscription (${s.plan_name})?\n\nUsers will lose access until reactivated. Audit-logged.`)) return;
        setBusy(s.id);
        try {
            await api.post(`/platform/subscriptions/${s.id}/suspend`);
            toast.success(`Suspended ${s.tenant_name}`);
            load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(null); }
    };

    const reactivate = async (s) => {
        if (!window.confirm(`Reactivate "${s.tenant_name}" subscription?\n\nFirm owner will be notified via email + push.`)) return;
        setBusy(s.id);
        try {
            await api.post(`/platform/subscriptions/${s.id}/reactivate`);
            toast.success(`Reactivated ${s.tenant_name}`);
            load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(null); }
    };

    const tableCtrl = useTableControls(items, {
        accessors: { autopay: (s) => (s.autopay ? "On" : "Off"), started: (s) => s.start_date || s.created_at },
    });

    return (
        <>
            <PageHeader title="Subscriptions" subtitle="Active, trialing and lapsed subscriptions across the platform. Suspend to revoke tenant access; reactivate to restore it."/>
            <div className="mb-3"><FilterChip ctrl={tableCtrl}/></div>
            {items.length === 0 ? (
                <EmptyState title="No subscriptions"/>
            ) : (
                <div className="border border-border overflow-x-auto">
                    <table className="min-w-[720px] w-full text-sm">
                        <thead className="bg-muted border-b border-border">
                            <tr className="text-left">
                                <SortableTh field="tenant_name" ctrl={tableCtrl}>Tenant</SortableTh>
                                <SortableTh field="plan_name" ctrl={tableCtrl}>Plan</SortableTh>
                                <SortableTh field="billing_cycle" ctrl={tableCtrl}>Cycle</SortableTh>
                                <SortableTh field="plan_price" ctrl={tableCtrl}>Price</SortableTh>
                                <SortableTh field="autopay" ctrl={tableCtrl}>AutoPay</SortableTh>
                                <SortableTh field="status" ctrl={tableCtrl}>Status</SortableTh>
                                <SortableTh field="started" ctrl={tableCtrl}>Started</SortableTh>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tableCtrl.rows.map(s => {
                                const suspended = ["past_due", "suspended", "cancelled"].includes(s.status);
                                return (
                                    <tr key={s.id} className="border-b border-border hover:bg-muted/50" data-testid={`sub-row-${s.id}`}>
                                        <td className="px-4 py-3">{s.tenant_name || "—"}</td>
                                        <td className="px-4 py-3">{s.plan_name || "—"}</td>
                                        <td className="px-4 py-3 text-muted-foreground capitalize">{s.billing_cycle?.replace(/_/g, " ")}</td>
                                        <td className="px-4 py-3 font-mono">₹{(s.plan_price ?? 0).toLocaleString()}</td>
                                        <td className="px-4 py-3 text-muted-foreground">{s.autopay ? "On" : "Off"}</td>
                                        <td className="px-4 py-3"><StatusBadge status={s.status}/></td>
                                        <td className="px-4 py-3 text-xs text-muted-foreground">{fmtDate(s.start_date || s.created_at)}</td>
                                        <td className="px-4 py-3 text-right">
                                            {busy === s.id ? (
                                                <Loader2 className="w-3.5 h-3.5 animate-spin inline text-muted-foreground"/>
                                            ) : suspended ? (
                                                <button onClick={() => reactivate(s)}
                                                    data-testid={`sub-reactivate-${s.id}`}
                                                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] text-emerald-500 hover:underline">
                                                    <Play className="w-3 h-3"/> Reactivate
                                                </button>
                                            ) : (
                                                <button onClick={() => suspend(s)}
                                                    data-testid={`sub-suspend-${s.id}`}
                                                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] text-rose-400 hover:underline">
                                                    <Pause className="w-3 h-3"/> Suspend
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}
